import React, { MouseEvent, useState } from 'react';
import ProgressBar from '../../common/ProgressBar';
import { useSelector } from 'react-redux';

// Ducks
import { RootState } from '../../../ducks/configureStore';
import classNames from 'classnames';
import { Table } from 'react-bootstrap';

import './AdaptivePerformanceReport.scss';

const strengthTopicTitle = 'Strength';
const focusTopicTitle = 'Area for Focus';
const notTied = 'Not tied with any other topics';
const none = 'None';
const tiedText = (tiesCount: number) =>
  tiesCount ? `Tied with ${tiesCount} other topic${tiesCount > 1 ? 's' : ''}` : notTied;

interface OpenRow {
  [key: string]: boolean;
}

const AdaptivePerformanceReport = () => {
  // REDUX STATE
  const { categories, score, bestTag, worstTag, bestTies, worstTies, instructions } = useSelector(
    (state: RootState) => {
      const { slidesLitmos } = state;
      const { currentSlide } = slidesLitmos;
      const { learnerData, instructions } = currentSlide || {};
      const { categories, bestScore, worstScore, tagsByScore, score, bestTag, worstTag } =
        learnerData || {};
      const bestTies = tagsByScore ? tagsByScore[bestScore as number] : undefined;
      const worstTies = tagsByScore ? tagsByScore[worstScore as number] : undefined;
      return {
        categories,
        score,
        bestTag,
        worstTag,
        currentSlide,
        bestTies,
        worstTies,
        instructions,
      };
    },
  );

  // LOCAL STATE
  const [openRows, setOpenRows] = useState({} as OpenRow);

  const onRowClick = (event: MouseEvent) => {
    if (event.currentTarget instanceof HTMLElement) {
      const id = event.currentTarget.id;
      const isOpen = openRows[id];

      setOpenRows({
        ...openRows,
        [id]: !isOpen,
      });
    }
  };

  const getTableRows = () => {
    if (categories) {
      return Object.keys(categories).map((key) => {
        const rows = [];
        const category = categories[key];
        const score = Math.round((category.totalCorrect / category.total) * 100);
        const isOpen = openRows[key];

        rows.push(
          <tr key={key} id={key} onClick={onRowClick} className="parent-row">
            <td>
              <div className="row-label">
                <i className="material-icons">{isOpen ? 'expand_more' : 'chevron_right'}</i>
                <span>{key}</span>
              </div>
            </td>
            <td>
              <ProgressBar
                value={score}
                showOnRight
                // These values added because typescript complained that they weren't passed
                label=""
                className=""
                showOnBar={false}
                small={false}
              />
            </td>
          </tr>,
        );

        if (isOpen) {
          const tags = category.tags.map((tag) => {
            if (tag.correct === undefined || !tag.total) {
              return null;
            }
            const tagScore = Math.round((tag.correct / tag.total) * 100);
            return (
              <tr key={tag.tag} className="child-row">
                <td>
                  <div className="row-label child">{tag.tag}</div>
                </td>
                <td>
                  <ProgressBar
                    value={tagScore}
                    showOnRight
                    small
                    // These values added because typescript complained that they weren't passed
                    label=""
                    className=""
                    showOnBar={false}
                  />
                </td>
              </tr>
            );
          });
          rows.push(...tags);
        }
        return rows;
      });
    }
  };

  const renderTopicBox = (isBest: boolean, tagText: string, ties: Tag[], topicTag: Tag) => {
    const tiesCount = ties && ties.length > 1 && tagText !== none ? ties.length - 1 : 0;
    const tiesText = <span className="italics">{tiedText(tiesCount)}</span>;
    const title = isBest ? strengthTopicTitle : focusTopicTitle;
    return (
      <div
        className={classNames('topic-box', {
          'best-box': isBest,
          'worst-box': !isBest,
        })}
      >
        <div className="topic-title">{title}</div>
        <div className="topic-name">{tagText}</div>
        <div className="topic-ties">{tiesText}</div>
      </div>
    );
  };

  const renderReport = () => {
    const tableRows = getTableRows();
    const bestTagText = bestTag ? `${bestTag.tag} (${bestTag.category})` : none;
    const worstTagText = worstTag ? `${worstTag.tag} (${worstTag.category})` : none;

    const bestTopicBox =
      bestTies && bestTag ? renderTopicBox(true, bestTagText, bestTies, bestTag) : null;

    const worstTopicBox =
      worstTies && worstTag ? renderTopicBox(false, worstTagText, worstTies, worstTag) : null;

    return (
      <div className="performance-report">
        <div className="report-header">
          <h1>Performance Report</h1>
          <div>
            {instructions ||
              `Congratulations on completing this module! Look for insights below that you can take into your work outside of this module.`}
          </div>
        </div>
        <div className="report-content">
          <Table hover size="sm">
            <thead>
              <tr className="report-table-header">
                <th />
                <th />
              </tr>
            </thead>
            <tbody>
              <tr className="overall-row">
                <td>
                  <div className="overall-header">Overall</div>
                </td>
                <td>
                  <ProgressBar
                    value={score}
                    showOnRight
                    // These values added because typescript complained that they weren't passed
                    label=""
                    className=""
                    showOnBar={false}
                    small={false}
                  />
                </td>
              </tr>
              {tableRows}
            </tbody>
          </Table>
          <div className="topic-boxes">
            {bestTopicBox}
            {worstTopicBox}
          </div>
        </div>
      </div>
    );
  };

  return <div className="AdaptivePerformanceReport">{renderReport()}</div>;
};

export default AdaptivePerformanceReport;
